<template>
  <div class="discussion-edit-component">
    <textarea
      v-model="inputValue"
      :data-key="inputKey"
      :placeholder="placeholder"
      :class="{ active }"
      @blur="blur"
      @focus="setInput"
      @keydown="grow($event)"
      @keyup="grow($event)"
    />
    <div class="focus-area">
      <button
        class="cancel"
        :disabled="submitting"
        @click="cancel"
      >
        CANCEL
      </button>
      <button
        v-if="editing"
        class="save"
        :disabled="submitting"
        @click="edit"
      >
        SAVE
      </button>
      <button
        v-else
        class="submit"
        :disabled="submitting"
        @click="create"
      >
        SUBMIT
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DiscussionEditor',
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    discussion: {
      type: Object,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    inputKey: {
      type: String,
    },
    parentDiscussion: {
      type: Object,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input-active', 'edit-discussion', 'cancel', 'create-discussion'],
  data() {
    return {
      inputValue: '',
    };
  },
  computed: {
    placeholder() {
      return 'Share your insight...';
    },
  },
  mounted() {
    this.grow({ target: this.$el.querySelector('textarea') });
    this.$watch('active', (newActive, oldActive) => {
      const deactivated = oldActive && !newActive;
      if (deactivated) {
        this.resetInput();
      }
    });
    this.$watch('discussion', (newDiscussion, oldDiscussion) => {
      if (newDiscussion?.message !== oldDiscussion?.message) {
        this.inputValue = newDiscussion.message;
      }
    }, { immediate: true });
  },
  methods: {
    blur() {
      this.$store.commit('appState/setInputtingText', false);
    },
    grow(event) {
      event.target.style.height = 'auto';
      event.target.style.height = `${Math.max(53, event.target.scrollHeight - 18)}px`;
    },
    setInput() {
      this.$store.commit('appState/setInputtingText', true);
      this.$emit('input-active');
    },
    edit() {
      const params = {
        message: this.inputValue,
        discussionId: this.discussion?.id,
      };
      this.$emit('edit-discussion', params);
    },
    cancel() {
      this.$emit('cancel');
    },
    create() {
      const params = {
        message: this.inputValue,
      };
      if (this.parentDiscussion) {
        if (this.parentDiscussion) {
          params.parentDiscussionId = this.parentDiscussion.id;
        }
      }
      this.$emit('create-discussion', params);
    },
    resetInput() {
      if (this.editing) {
        this.inputValue = this.discussion?.message || '';
      } else {
        this.inputValue = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.discussion-edit-component {
  margin: rem-calc(16) 0;

  textarea {
    border: 1px solid rgba(205, 213, 223, 1);
    border-radius: 8px;
    box-sizing: border-box;
    color: #7f8fa4;
    display: block;
    font-size: rem-calc(16);
    overflow: hidden;
    padding: rem-calc(16);
    resize: none;
    width: 100%;

    @include focus-not-visible {
      &:focus, &.active {
        border-bottom: 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-color: #36a5f8;
        outline: 0;

        + .focus-area {
          display: flex;
        }
      }
    }
  }

  .focus-area {
    background: #E7F4FE;
    border: 1px solid #36a5f8;
    border-top: 0;
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    box-sizing: border-box;
    display: none;
    padding: rem-calc(4);

    &.edit {
      margin-bottom: rem-calc(16);
    }

    button {
      background: #7bd56e;
      border-radius: 3px;
      color: white;
      display: block;
      font-size: rem-calc(8);
      font-weight: $semi-bold;
      margin-left: auto;
      padding: rem-calc(6);

      &:disabled {
        opacity: 0.6;
      }

      &.save,
      &.submit {
        margin-left: rem-calc(8);
      }

      &.cancel {
        background: #FA726F;
        margin-left: auto;
      }
    }
  }
}
</style>
